/**
 * DashboardCalendar Component
 * Self-contained calendar widget that fetches its own data from API
 *
 * Features:
 * - Full month view (~320px width)
 * - Click on day to see events for that day
 * - Event indicators on days with events
 * - Add/Edit/Delete events with modal
 * - Autonomous - uses API hooks internally
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  PlusIcon,
  MapPinIcon,
  UserIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { monthNames, weekDays, eventCategories } from '@/constants/calendar';
import { getDaysInMonth } from '@/utils/calendar/dateHelpers';
import { EventModal } from '@/components/CalendarPage/EventModal';
import { CalendarEvent } from '@/types/calendar';
import type {
  CalendarLeadContext,
  CalendarClientContext,
  CalendarQuoteContext,
} from '@/shared/components/CalendarModal';

// Contexts
import { useAuth } from '@/shared/contexts/AuthContext';
import { useTenant } from '@/shared/contexts/TenantContext';

// API Hooks
import {
  useCalendarView,
  useCreateCalendarEvent,
  useUpdateCalendarEvent,
  useDeleteCalendarEvent,
} from '@/hooks/useCalendarAPI';
import { useHolidayMap } from '@/hooks/useHolidays';
import { useQuery } from '@tanstack/react-query';
import { calendarService, type CalendarFeedEventInstance } from '@/services/api/calendarService';
import {
  mapAPIEventsToFrontend,
  mapFrontendEventToAPICreate,
  mapFrontendEventToAPIUpdate,
} from '@/utils/calendar/eventMapper';

// Types
interface DashboardCalendarProps {
  /** Optional callback when clicking on an event (for navigation) */
  onEventClick?: (event: CalendarEvent) => void;
  /** Optional callback when date selection changes */
  onDateChange?: (date: Date) => void;
  /** Initial selected date */
  initialDate?: Date;
  /** Show compact version */
  compact?: boolean;
  /** Lead context - pre-fills event with lead info */
  leadContext?: CalendarLeadContext;
  /** Client context - pre-fills event with client info */
  clientContext?: CalendarClientContext;
  /** Quote context - adds quote reference to event */
  quoteContext?: CalendarQuoteContext;
  /**
   * Layout variant:
   * - 'sidebar': Vertical layout for Dashboard 2.0 sidebar (calendar on top, events below)
   * - 'horizontal': Horizontal 2-column layout (default, used in modals)
   */
  variant?: 'sidebar' | 'horizontal';
  /** Minimum selectable date (YYYY-MM-DD) — days before this are disabled */
  minDate?: string;
  /** Maximum selectable date (YYYY-MM-DD) — days after this are disabled */
  maxDate?: string;
  /** Callback when a new event is created (passes the event start date) */
  onEventCreated?: (date: Date) => void;
  /** Hide add/edit event buttons — calendar is view-only for date selection */
  readOnly?: boolean;
}

// Event type colors
const eventTypeColors: Record<string, string> = {
  call: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  meeting: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  visit: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800',
  'follow-up': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
  launch: 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 border-pink-200 dark:border-pink-800',
  presentation: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
  training: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800',
  other: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700',
};

const eventTypeLabels: Record<string, string> = {
  call: 'Llamada',
  meeting: 'Reunión',
  visit: 'Visita',
  'follow-up': 'Seguimiento',
  launch: 'Lanzamiento',
  presentation: 'Presentación',
  training: 'Capacitación',
  other: 'Otro',
};

export const DashboardCalendar: React.FC<DashboardCalendarProps> = ({
  onEventClick,
  onDateChange,
  initialDate,
  compact = false,
  leadContext,
  clientContext,
  quoteContext,
  variant = 'horizontal',
  minDate,
  maxDate,
  onEventCreated,
  readOnly = false,
}) => {
  // ===========================
  // CONTEXTS
  // ===========================
  const { user } = useAuth();
  const { selectedProject, effectiveCompanyId, isPlatform, selectedAdvisor } = useTenant();

  // ===========================
  // STATE
  // ===========================
  const [currentMonth, setCurrentMonth] = useState(initialDate || new Date());
  const [selectedDate, setSelectedDate] = useState(initialDate || new Date());

  // Modal states
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showStartDateCalendar, setShowStartDateCalendar] = useState(false);
  const [showEndDateCalendar, setShowEndDateCalendar] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  // New event form state
  const [newEvent, setNewEvent] = useState<Partial<CalendarEvent>>({
    title: '',
    description: '',
    categoryId: 'meeting-presencial',
    type: 'meeting',
    startTime: new Date(),
    endTime: new Date(),
    location: '',
    meetingLink: '',
    status: 'scheduled',
    source: 'manual',
  });

  // ===========================
  // API HOOKS
  // ===========================
  // Calculate current month in YYYY-MM format for API
  const currentMonthStr = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }, [currentMonth]);

  // Fetch events for current month
  // Platform users (OWLY): filter by selected advisor (if selected) or show all events
  // Tenant users: filter by their own user ID
  const { data: calendarViewData, isLoading } = useCalendarView(
    currentMonthStr,
    isPlatform ? (selectedAdvisor?.id || undefined) : user?.id,  // Platform users filter by selected advisor
    isPlatform ? (effectiveCompanyId || undefined) : undefined,  // Platform users filter by selected company
    selectedProject?.id  // Both filter by selected project
  );

  // Mutations
  const createEventMutation = useCreateCalendarEvent();
  const updateEventMutation = useUpdateCalendarEvent();
  const deleteEventMutation = useDeleteCalendarEvent();

  // Convert API events to frontend format
  const events = useMemo<CalendarEvent[]>(() => {
    if (calendarViewData?.events_by_date) {
      const allEvents: CalendarEvent[] = [];
      Object.values(calendarViewData.events_by_date).forEach((dayEvents) => {
        allEvents.push(...mapAPIEventsToFrontend(dayEvents));
      });
      return allEvents;
    }
    return [];
  }, [calendarViewData]);

  // ===========================
  // EFFECTS
  // ===========================
  // Reset form when selectedEvent changes
  useEffect(() => {
    if (selectedEvent) {
      setNewEvent({
        title: selectedEvent.title,
        description: selectedEvent.description || '',
        categoryId: selectedEvent.categoryId,
        type: selectedEvent.type,
        startTime: selectedEvent.startTime,
        endTime: selectedEvent.endTime,
        location: selectedEvent.location || '',
        meetingLink: selectedEvent.meetingLink || '',
        leadId: selectedEvent.leadId,
        leadName: selectedEvent.leadName,
        status: selectedEvent.status,
        source: selectedEvent.source,
      });
    } else {
      // Calculate smart default times - next 30 minute slot
      const now = new Date();
      const isToday = selectedDate.toDateString() === now.toDateString();

      const startTime = new Date(selectedDate);
      if (isToday) {
        // Round up to next 30 minute slot
        const currentMinutes = now.getMinutes();
        const roundedMinutes = Math.ceil(currentMinutes / 30) * 30;
        if (roundedMinutes >= 60) {
          startTime.setHours(now.getHours() + 1, 0, 0, 0);
        } else {
          startTime.setHours(now.getHours(), roundedMinutes, 0, 0);
        }
        // If the resulting time is still in the past (edge case), add 30 mins
        if (startTime < now) {
          startTime.setMinutes(startTime.getMinutes() + 30);
        }
      } else {
        // For future dates, default to 9:00 AM
        startTime.setHours(9, 0, 0, 0);
      }

      // End time is always 30 minutes after start
      const endTime = new Date(startTime);
      endTime.setMinutes(startTime.getMinutes() + 30);

      // Get context name for title pre-fill
      const contextName = leadContext?.name || clientContext?.name;
      const quoteInfo = quoteContext ? ` - Cotización ${quoteContext.number}` : '';

      setNewEvent({
        title: contextName ? `Reunión con ${contextName}${quoteInfo}` : '',
        description: quoteContext?.unitName ? `Unidad: ${quoteContext.unitName}` : '',
        categoryId: 'meeting-presencial',
        type: 'meeting',
        startTime,
        endTime,
        location: '',
        meetingLink: '',
        status: 'scheduled',
        source: 'manual',
        // Pre-fill lead context
        leadId: leadContext?.id,
        leadName: leadContext?.name,
      });
    }
  }, [selectedEvent, selectedDate, leadContext, clientContext, quoteContext]);

  // Notify parent of date changes
  useEffect(() => {
    onDateChange?.(selectedDate);
  }, [selectedDate, onDateChange]);

  // ===========================
  // HELPERS
  // ===========================
  const days = getDaysInMonth(currentMonth);

  // Holiday map for current month
  const holidayMap = useHolidayMap(currentMonth.getFullYear(), selectedProject?.country);

  const getHolidayName = (day: number | null): string | null => {
    if (!day) return null;
    const m = String(currentMonth.getMonth() + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return holidayMap.get(`${currentMonth.getFullYear()}-${m}-${d}`) || null;
  };

  // ───────────────────────────────────────────────────────────────────
  // EVENT INSTANCES — ferias del proyecto que aparecen en el calendario.
  // Color naranja distinto de festivos (azul) y citas (puntos primary).
  // Vienen del feed unificado /api/calendar/events/feed/.
  // ───────────────────────────────────────────────────────────────────
  const monthYYYYMM = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
  const { data: feedData } = useQuery({
    queryKey: ['calendar-feed', monthYYYYMM, selectedProject?.id],
    queryFn: () => calendarService.getCalendarFeed({
      month: monthYYYYMM,
      project: selectedProject!.id,
    }),
    enabled: !!selectedProject?.id,
    staleTime: 60 * 1000,
  });
  const eventInstances: CalendarFeedEventInstance[] = feedData?.event_instances || [];

  // Map: day-of-month → array de EventInstance que cubren ese día
  // (un event con start/end abarca múltiples días → poblamos todos).
  const eventsByDayMap = useMemo(() => {
    const map = new Map<number, CalendarFeedEventInstance[]>();
    const yr = currentMonth.getFullYear();
    const mo = currentMonth.getMonth();
    for (const inst of eventInstances) {
      if (!inst.start_date) continue;
      const start = new Date(inst.start_date + 'T00:00:00');
      const end = inst.end_date ? new Date(inst.end_date + 'T00:00:00') : start;
      // Iterar día a día solo dentro del mes actual
      let cursor = new Date(start);
      while (cursor <= end) {
        if (cursor.getFullYear() === yr && cursor.getMonth() === mo) {
          const day = cursor.getDate();
          if (!map.has(day)) map.set(day, []);
          map.get(day)!.push(inst);
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    return map;
  }, [eventInstances, currentMonth]);

  const getEventInstancesForDay = (day: number | null): CalendarFeedEventInstance[] => {
    if (!day) return [];
    return eventsByDayMap.get(day) || [];
  };

  // ── Helpers para el panel del día seleccionado ──
  // Obtienen holiday + event instances del día actualmente seleccionado para
  // mostrarlos como cards prominentes (encima de las citas normales). Esto
  // resuelve el caso "veo el día en color pero no sé qué pasa".
  const selectedDayHolidayName = (() => {
    const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const d = String(selectedDate.getDate()).padStart(2, '0');
    return holidayMap.get(`${selectedDate.getFullYear()}-${m}-${d}`) || null;
  })();
  const selectedDayEventInstances: CalendarFeedEventInstance[] = (() => {
    // Solo las que cubren el día seleccionado (start_date <= day <= end_date)
    const result: CalendarFeedEventInstance[] = [];
    const sel = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    for (const inst of eventInstances) {
      if (!inst.start_date) continue;
      const start = new Date(inst.start_date + 'T00:00:00');
      const end = inst.end_date ? new Date(inst.end_date + 'T00:00:00') : start;
      if (sel >= start && sel <= end) result.push(inst);
    }
    return result;
  })();

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const isToday = (day: number | null) => {
    if (!day) return false;
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === currentMonth.getMonth() &&
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isSelected = (day: number | null) => {
    if (!day) return false;
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentMonth.getMonth() &&
      selectedDate.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isDateDisabled = (day: number | null): boolean => {
    if (!day) return true;
    if (!minDate && !maxDate) return false;
    const m = String(currentMonth.getMonth() + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    const dateStr = `${currentMonth.getFullYear()}-${m}-${d}`;
    if (minDate && dateStr < minDate) return true;
    if (maxDate && dateStr > maxDate) return true;
    return false;
  };

  const getEventsForDay = (day: number | null): CalendarEvent[] => {
    if (!day) return [];
    const dateStr = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    ).toDateString();

    return events.filter(event => {
      const eventDate = new Date(event.startTime).toDateString();
      return eventDate === dateStr;
    });
  };

  const handleDayClick = (day: number) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(newDate);
  };

  // ===========================
  // MODAL HANDLERS
  // ===========================
  const handleAddEvent = () => {
    setSelectedEvent(null);
    setShowEventModal(true);
  };

  const handleEventClick = (event: CalendarEvent) => {
    if (onEventClick) {
      onEventClick(event);
    } else {
      setSelectedEvent(event);
      setShowEventModal(true);
    }
  };

  const handleSaveEvent = async () => {
    if (!newEvent.title || !newEvent.startTime || !newEvent.endTime) return;

    // Platform users must have a company selected to create events
    if (isPlatform && !effectiveCompanyId && !selectedEvent) {
      // Import toast dynamically to avoid circular deps
      const { toast } = await import('sonner');
      toast.error('Selecciona una empresa', {
        description: 'Para crear eventos, primero selecciona una empresa en el filtro superior.',
      });
      return;
    }

    try {
      if (selectedEvent) {
        // Update existing event
        const apiData = mapFrontendEventToAPIUpdate(newEvent);
        await updateEventMutation.mutateAsync({
          id: selectedEvent.id,
          data: apiData,
        });
      } else {
        // Create new event - include client, project, company, and user context
        // Platform users (OWLY) need to specify company and can assign to specific advisor
        const apiData = mapFrontendEventToAPICreate(newEvent, {
          clientId: clientContext?.id,
          projectId: selectedProject?.id,
          companyId: isPlatform ? (effectiveCompanyId || undefined) : undefined,
          userId: isPlatform ? (selectedAdvisor?.id || undefined) : undefined,
        });
        await createEventMutation.mutateAsync(apiData);
        // Notify parent that an event was created with this date
        if (newEvent.startTime && onEventCreated) {
          onEventCreated(new Date(newEvent.startTime));
        }
      }
      setShowEventModal(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    try {
      await deleteEventMutation.mutateAsync(selectedEvent.id);
      setShowEventModal(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  // Direct delete from card (without opening modal)
  const handleDirectDeleteEvent = async (e: React.MouseEvent, eventId: string) => {
    e.stopPropagation(); // Prevent opening the modal
    try {
      await deleteEventMutation.mutateAsync(eventId);
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  // Get events for selected date
  const selectedDayEvents = getEventsForDay(selectedDate.getDate());

  // Format selected date
  const formatSelectedDate = () => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    };
    return selectedDate.toLocaleDateString('es-ES', options);
  };

  const isMutating = createEventMutation.isPending || updateEventMutation.isPending || deleteEventMutation.isPending;

  // ===========================
  // RENDER
  // ===========================
  const isSidebar = variant === 'sidebar';

  return (
    <>
      <div className={isSidebar ? 'h-full flex flex-col' : ''}>
        {/* Calendar Header */}
        <div className={`px-4 py-3 border-b border-gray-200 dark:border-gray-800 ${isSidebar ? 'flex-shrink-0' : ''}`}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <CalendarDaysIcon className="h-4 w-4 text-primary-500" />
              Calendario
              {isLoading && (
                <span className="w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              )}
            </h3>
            {!readOnly && <button
              onClick={handleAddEvent}
              disabled={isMutating}
              className="p-1.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg transition-colors"
              title="Agregar evento"
            >
              <PlusIcon className="h-4 w-4" />
            </button>}
          </div>
        </div>

        {/* Main Content - Layout depends on variant */}
        <div className={
          isSidebar
            ? 'flex-1 flex flex-col min-h-0 overflow-hidden'
            : 'grid grid-cols-2 h-[400px]'
        }>
          {/* Calendar Section */}
          <div className={
            isSidebar
              ? 'flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-800'
              : 'border-r border-gray-200 dark:border-gray-800 p-4 h-full'
          }>
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={() => navigateMonth('prev')}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ChevronLeftIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </button>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </span>
              <button
                onClick={() => navigateMonth('next')}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ChevronRightIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Week Days Header */}
            <div className="grid grid-cols-7 mb-1">
              {weekDays.map(day => (
                <div
                  key={day}
                  className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1"
                >
                  {day.slice(0, 2)}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-0.5">
              {days.map((day, index) => {
                const dayEvents = getEventsForDay(day);
                const hasEvents = dayEvents.length > 0;
                const holidayName = getHolidayName(day);
                const dayEventInstances = getEventInstancesForDay(day);
                const hasEventInstance = dayEventInstances.length > 0;
                const disabled = !day || isDateDisabled(day);

                // Tooltip rico — combina festivo + ferias + cantidad citas
                const tooltipParts: string[] = [];
                if (holidayName) tooltipParts.push(`🇨🇴 Festivo: ${holidayName}`);
                if (hasEventInstance) {
                  for (const e of dayEventInstances) {
                    tooltipParts.push(
                      `📅 ${e.template_name} — ${e.name}` +
                      (e.location ? ` (${e.location})` : '')
                    );
                  }
                }
                if (hasEvents) {
                  tooltipParts.push(`✓ ${dayEvents.length} ${dayEvents.length === 1 ? 'cita' : 'citas'} programada${dayEvents.length === 1 ? '' : 's'}`);
                }
                const tooltipText = tooltipParts.length > 0 ? tooltipParts.join('\n') : undefined;

                return (
                  <button
                    key={index}
                    onClick={() => day && !disabled && handleDayClick(day)}
                    disabled={disabled}
                    title={tooltipText}
                    className={`
                      aspect-square flex flex-col items-center justify-center rounded-md text-sm font-medium
                      transition-all relative
                      ${!day ? 'invisible' : ''}
                      ${disabled && day
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed opacity-50'
                        : isSelected(day)
                          ? 'bg-primary-500 text-white shadow-md'
                          : isToday(day)
                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-bold ring-1 ring-primary-300 dark:ring-primary-700'
                            : hasEventInstance
                              ? 'bg-orange-50 dark:bg-orange-950/20 text-orange-700 dark:text-orange-400 font-semibold hover:bg-orange-100 dark:hover:bg-orange-950/40'
                              : holidayName
                                ? 'bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400 font-semibold hover:bg-blue-100 dark:hover:bg-blue-950/40'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                      }
                    `}
                  >
                    {day}
                    {hasEvents && !holidayName && !hasEventInstance && (
                      <div className="flex gap-0.5 mt-0.5">
                        {dayEvents.slice(0, 3).map((_, i) => (
                          <div
                            key={i}
                            className={`w-1 h-1 rounded-full ${
                              isSelected(day) ? 'bg-white' : 'bg-primary-500'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                    {hasEventInstance && !isSelected(day) && (
                      <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-orange-500" />
                    )}
                    {holidayName && !isSelected(day) && !hasEventInstance && (
                      <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-500" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Legend — colores explicativos. Solo se muestran las que aplican
                (si no hay festivos en el mes, no muestra la leyenda de festivos).
                Ayuda al user a entender de un vistazo qué significa cada color. */}
            {(holidayMap.size > 0 || eventInstances.length > 0) && (
              <div className="mt-3 flex items-center justify-center gap-4 text-[10px] text-gray-500 dark:text-gray-400">
                {holidayMap.size > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    <span>Festivo</span>
                  </div>
                )}
                {eventInstances.length > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-orange-500" />
                    <span>Feria / Evento</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-primary-500" />
                  <span>Cita / Tarea</span>
                </div>
              </div>
            )}
          </div>

          {/* Events Section (right in horizontal, below in sidebar) */}
          <div className={
            isSidebar
              ? 'flex-1 p-4 flex flex-col overflow-hidden min-h-0'
              : 'p-4 h-full flex flex-col overflow-hidden'
          }>
            <div className="flex-shrink-0 flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white capitalize">
                {formatSelectedDate()}
              </h4>
              {(() => {
                const total = selectedDayEvents.length + selectedDayEventInstances.length + (selectedDayHolidayName ? 1 : 0);
                return total > 0 ? (
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {total} {total === 1 ? 'item' : 'items'}
                  </span>
                ) : null;
              })()}
            </div>

            {selectedDayEvents.length === 0 && !selectedDayHolidayName && selectedDayEventInstances.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center">
                <CalendarDaysIcon className="h-10 w-10 text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Sin eventos programados
                </p>
                {!readOnly && (
                  <button
                    onClick={handleAddEvent}
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary-600 dark:text-primary-400 font-medium bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Agregar evento
                  </button>
                )}
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto space-y-2">
                {/* HOLIDAY card — info-only, no clickable */}
                {selectedDayHolidayName && (
                  <div className="p-2.5 rounded-lg border bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200">
                    <div className="flex items-start justify-between mb-1">
                      <span className="text-xs font-medium uppercase tracking-wider opacity-75">
                        Festivo
                      </span>
                    </div>
                    <h5 className="font-medium text-sm">{selectedDayHolidayName}</h5>
                    <div className="flex items-center gap-1.5 text-xs opacity-75 mt-0.5">
                      <CalendarDaysIcon className="h-3 w-3" />
                      Día no laboral
                    </div>
                  </div>
                )}

                {/* EVENT INSTANCES (ferias del proyecto) */}
                {selectedDayEventInstances.map(inst => (
                  <div
                    key={inst.id}
                    className="p-2.5 rounded-lg border bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800 text-orange-900 dark:text-orange-200"
                  >
                    <div className="flex items-start justify-between mb-1">
                      <span className="text-xs font-medium uppercase tracking-wider opacity-75">
                        Feria · {inst.template_name}
                      </span>
                      {inst.leads_count > 0 && (
                        <span className="text-xs opacity-75 font-medium">
                          {inst.leads_count} leads
                        </span>
                      )}
                    </div>
                    <h5 className="font-medium text-sm mb-1">{inst.name}</h5>
                    <div className="space-y-0.5">
                      {(inst.start_date || inst.end_date) && (
                        <div className="flex items-center gap-1.5 text-xs opacity-75">
                          <CalendarDaysIcon className="h-3 w-3" />
                          {inst.start_date}
                          {inst.end_date && inst.end_date !== inst.start_date && ` → ${inst.end_date}`}
                        </div>
                      )}
                      {inst.location && (
                        <div className="flex items-center gap-1.5 text-xs opacity-75">
                          <MapPinIcon className="h-3 w-3" />
                          {inst.location}
                        </div>
                      )}
                      {inst.template_organizer && (
                        <div className="flex items-center gap-1.5 text-xs opacity-75">
                          <UserIcon className="h-3 w-3" />
                          {inst.template_organizer}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Citas normales (CalendarEvent) */}
                {selectedDayEvents.map(event => (
                  <div
                    key={event.id}
                    onClick={() => !readOnly && handleEventClick(event)}
                    className={`p-2.5 rounded-lg border ${readOnly ? '' : 'cursor-pointer hover:shadow-sm'} transition-shadow relative group ${
                      eventTypeColors[event.type] || eventTypeColors.other
                    }`}
                  >
                    {/* Delete button */}
                    {!readOnly && <button
                      onClick={(e) => handleDirectDeleteEvent(e, event.id)}
                      disabled={deleteEventMutation.isPending}
                      className="absolute top-1.5 right-1.5 p-1 rounded-md bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-200 dark:hover:bg-red-900/50 transition-all"
                      title="Eliminar evento"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>}
                    <div className="flex items-start justify-between mb-1 pr-6">
                      <span className="text-xs font-medium uppercase tracking-wider opacity-75">
                        {eventTypeLabels[event.type] || 'Evento'}
                      </span>
                      <span className="text-xs opacity-75">
                        {new Date(event.startTime).toLocaleTimeString('es-ES', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <h5 className="font-medium text-sm mb-1">{event.title}</h5>
                    <div className="space-y-0.5">
                      {event.leadName && (
                        <div className="flex items-center gap-1.5 text-xs opacity-75">
                          <UserIcon className="h-3 w-3" />
                          {event.leadName}
                        </div>
                      )}
                      {event.location && (
                        <div className="flex items-center gap-1.5 text-xs opacity-75">
                          <MapPinIcon className="h-3 w-3" />
                          {event.location}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Event Modal */}
      <EventModal
        show={showEventModal}
        selectedEvent={selectedEvent}
        newEvent={newEvent}
        eventCategories={eventCategories}
        showStartDateCalendar={showStartDateCalendar}
        showEndDateCalendar={showEndDateCalendar}
        showStartTimePicker={showStartTimePicker}
        showEndTimePicker={showEndTimePicker}
        onClose={() => {
          setShowEventModal(false);
          setSelectedEvent(null);
        }}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
        onNewEventChange={setNewEvent}
        setShowStartDateCalendar={setShowStartDateCalendar}
        setShowEndDateCalendar={setShowEndDateCalendar}
        setShowStartTimePicker={setShowStartTimePicker}
        setShowEndTimePicker={setShowEndTimePicker}
        leadContext={leadContext}
        clientContext={clientContext}
        quoteContext={quoteContext}
      />
    </>
  );
};
